import {camelToSnakeCase} from "../../utils/common";

export class BaseQuestionnaireFormData {
    constructor({
        HeightModel,
        WeightModel,
        questionnaireConstants = {},
        additionalFields = {},
        mainGoal = "weight_loss",
        mainGoalCode = 0,
        mainGoalLokalise = "Lose weight",
        workoutName = "",
        mode = ""
    }) {
        Object.defineProperty(this, "questionnaireMeta", {
            value: {
                questionnaireConstants,
                mainGoal,
                mainGoalCode,
                mainGoalLokalise,
                workoutName,
                mode
            },
            enumerable: false,
            writable: true
        });

        this.curIndex = 0;
        this.ageRange = "";
        this.userScale = "";
        this.gender = "";
        this.currentBodyType = "";
        this.targetBodyType = "";
        this.height = new HeightModel("", "", "ft");
        this.currentWeight = new WeightModel("", "lbs");
        this.targetWeight = new WeightModel("", "lbs");
        this.age = "";
        this.lastingResults = "";
        this.activityLevel = "";
        this.resultsTiming = "";
        this.healthConcerns = "";
        this.achieveGoal = "";
        this.loseWeightFaster = "";
        this.waterIntake = "";
        this.mealTime = "";
        this.lastMealTime = "";
        this.mealCount = "";
        this.betterFood = "";
        this.foodRestrictions = "";
        this.foodsOftenEat = "";
        this.foodsDislikeEat = "";
        this.dietaryPreference = "";
        this.otherBenefits = "";
        this.craveFood = "";
        this.keepRecord = "";
        this.knowNutrients = "";
        this.foodScan = "";
        this.illustratedOne = "";
        this.illustratedTwo = "";
        this.illustratedThree = "";
        this.illustratedFour = "";
        this.userPower = "";
        this.creatingPlan = "";
        this.email = "";
        this.userName = "";
        this.profileSummary = "";
        this.pay = "";

        Object.entries(additionalFields).forEach(([key, value]) => {
            this[key] = value;
        });
    }

    getQuestionnaireMeta() {
        return this.questionnaireMeta || {};
    }

    getQuestionnaireConstants() {
        return this.getQuestionnaireMeta().questionnaireConstants || {};
    }

    toPlainObject() {
        const plainObject = {
            main_goal: this.getQuestionnaireMeta().mainGoal
        };

        for (const key in this) {
            if (Object.prototype.hasOwnProperty.call(this, key) && typeof this[key] !== "function") {
                if (key === "height" || key === "currentWeight" || key === "targetWeight") {
                    plainObject[camelToSnakeCase(key)] = this[key].toPlainObject();
                } else {
                    plainObject[camelToSnakeCase(key)] = this[key];
                }
            }
        }

        return plainObject;
    }

    loadFromPlainObject(data) {
        if (!data || typeof data !== "object") {
            debugger;
            return;
        }

        for (const key in this) {
            const snakeKey = camelToSnakeCase(key);
            if (Object.prototype.hasOwnProperty.call(this, key) && snakeKey in data) {
                if (key === "curIndex") {
                    const parsedIndex = parseInt(data[snakeKey], 10);
                    if (!isNaN(parsedIndex)) {
                        this[key] = parsedIndex;
                    }
                } else if (key === "height" || key === "currentWeight" || key === "targetWeight") {
                    this[key] = this[key].create(data[snakeKey]);
                } else {
                    this[key] = data[snakeKey];
                }
            }
        }
    }

    fromPlainObject(data) {
        const instance = new this.constructor();
        instance.loadFromPlainObject(data);
        return instance;
    }

    clone() {
        return this.fromPlainObject(this.toPlainObject());
    }

    toUserProfile(userId) {
        return JSON.stringify({
            health_concerns: this.getHealthConcerns(),
            weight_unit: this.currentWeight.getUnit(),
            current_weight: this.currentWeight.weight,
            allergies: this.getAllergy(),
            age: this.age,
            activity_level: this.getActivityLevel(),
            height_unit: this.height.getUnit(),
            favorite_foods: this.getFavoriteFoods(),
            dislike_foods: this.getDislikeFoods(),
            user_id: userId,
            dietary_preference: this.getDietaryPreference(),
            height: this.height.getHeight(),
            target_weight: this.targetWeight.weight,
            meal_count: this.getMealCount(),
            gender: this.getGender(),
            main_goal: this.getMainGoal()
        });
    }

    getGender() {
        if (this.gender === "female") {
            return 2;
        } else if (this.gender === "male") {
            return 1;
        }
        return 0;
    }

    getIndexsByList(values = [], list = []) {
        const res = [];
        list.forEach((item, index) => {
            if (item?.value) {
                if (values.includes(item.value)) {
                    res.push(index);
                }
            } else if (item?.content) {
                if (values.includes(item.content)) {
                    res.push(index);
                }
            } else if (values.includes(item)) {
                res.push(index);
            }
        });
        return res;
    }

    getIndexByList(value, list = []) {
        let res = undefined;
        list.forEach((item, index) => {
            if (item?.value) {
                if (value === item.value) {
                    res = index;
                }
            } else if (item?.content) {
                if (value === item.content) {
                    res = index;
                }
            } else if (value === item) {
                res = index;
            }
        });
        return res;
    }

    getHealthConcerns() {
        return this.getIndexsByList(this.healthConcerns, this.getQuestionnaireConstants().HEALTH_CONCERNS);
    }

    getDietaryPreference() {
        return this.getIndexByList(this.dietaryPreference, this.getQuestionnaireConstants().DIETARY_PREFERENCE);
    }

    getActivityLevel() {
        return this.getIndexByList(this.activityLevel, this.getQuestionnaireConstants().ACTIVITY_LEVEL);
    }

    getResultsTiming() {
        return this.getIndexByList(this.resultsTiming, this.getQuestionnaireConstants().RESULTS_TIMING);
    }

    getMainGoal() {
        return this.getQuestionnaireMeta().mainGoalCode ?? 0;
    }

    getMainGoalLokalise() {
        return this.getQuestionnaireMeta().mainGoalLokalise || "";
    }

    getWorkoutName() {
        return this.getQuestionnaireMeta().workoutName || "";
    }

    getMode() {
        return this.getQuestionnaireMeta().mode || "";
    }

    getMealCount() {
        return this.getIndexByList(this.mealCount, this.getQuestionnaireConstants().MEAL_COUNT);
    }

    getAllergy() {
        return this.getIndexsByList(this.foodRestrictions, this.getQuestionnaireConstants().ALLERGY);
    }

    getFavoriteFoods() {
        return this.getIndexsByList(this.foodsOftenEat, this.getQuestionnaireConstants().FOOD_OFTEN_EAT);
    }

    getDislikeFoods() {
        return this.getIndexsByList(this.foodsDislikeEat, this.getQuestionnaireConstants().FOOD_DISLIKE_EAT);
    }
}

export default BaseQuestionnaireFormData;
